/**
 * TokenRefresher
 *
 * A singleton that holds a registered silent-refresh callback so that
 * non-hook code (e.g. fetchService) can request a token refresh without
 * direct access to the Auth0 SDK.
 *
 * Usage:
 *   // Register once, inside TokenHandler on mount:
 *   tokenRefresher.register(getAccessTokenSilently);
 *
 *   // Call from anywhere:
 *   const newToken = await tokenRefresher.tryRefresh();
 *   if (newToken) { ... } else { // refresh failed }
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('TOKEN_REFRESHER');

type RefreshFn = (options?: any) => Promise<string>;

class TokenRefresher {
  private refreshFn: RefreshFn | null = null;
  private audience: string = '';
  // In-flight promise deduplication: concurrent callers share one refresh attempt
  private inFlightRefresh: Promise<string | null> | null = null;

  register(fn: RefreshFn, audience: string): void {
    this.refreshFn = fn;
    this.audience = audience;
    log.log('Refresh function registered');
  }

  /**
   * Attempt a silent token refresh.
   * Concurrent calls are coalesced into a single Auth0 request to avoid
   * refresh token rotation races when multiple 401s arrive simultaneously.
   * Returns the new access token on success, or null on failure.
   * Also stores the new token in localStorage['authToken'] on success.
   */
  async tryRefresh(): Promise<string | null> {
    if (!this.refreshFn) {
      log.warn('No refresh function registered');
      return null;
    }

    // Return the existing in-flight promise if one is already running
    if (this.inFlightRefresh) {
      log.log('Refresh already in progress — coalescing request');
      return this.inFlightRefresh;
    }

    this.inFlightRefresh = this._doRefresh().finally(() => {
      this.inFlightRefresh = null;
    });

    return this.inFlightRefresh;
  }

  private async _doRefresh(): Promise<string | null> {
    try {
      log.log('Attempting silent token refresh...');
      const newToken = await this.refreshFn!({
        authorizationParams: {
          audience: this.audience,
          scope: 'openid profile email offline_access',
        },
      });

      if (newToken) {
        localStorage.setItem('authToken', newToken);
        window.dispatchEvent(new Event('auth-refresh'));
        log.log('Silent token refresh succeeded');
        return newToken;
      }

      return null;
    } catch (error) {
      log.warn('Silent token refresh failed:', error);
      return null;
    }
  }
}

export const tokenRefresher = new TokenRefresher();
export default tokenRefresher;
